import { ApolloClient, type DocumentNode } from "@apollo/client";
import { MasterDataGraphQlAdapter } from "@src/infrastructure/adapters/graphQl/MasterDataGraphQlAdapter";
import {
  CREATE_MASTER_DATA_PURCHASER,
  GET_MASTER_DATA_PURCHASERS,
  UPDATE_MASTER_DATA_PURCHASER,
} from "@src/domain/graphql/masterData";
import {
  MasterDataPurchaser,
  type IMasterDataPurchaser,
} from "@src/domain/entities/masterData";
import type {
  CreateMasterDataPurchaserDTO,
  GetMasterDataPurchasersDTO,
  UpdateMasterDataPurchaserDTO,
} from "@src/domain/dto/masterData";
import type {
  IMasterDataPurchaserRepository,
  MasterDataPurchasersListResult,
} from "@src/domain/repositories/masterData";
import {
  requireMasterDataMutationData,
  throwIfMasterDataGraphQLError,
  throwIfMasterDataMutationError,
} from "./masterDataRepository.shared";

function mapPurchaserItem(item: IMasterDataPurchaser): MasterDataPurchaser {
  return MasterDataPurchaser.from({
    purchaserId: item.purchaserId,
    purchaserKey: item.purchaserKey,
    purchaserName: item.purchaserName,
    isActive: item.isActive,
    metadata: item.metadata ?? null,
    searchKeywords: item.searchKeywords ?? null,
    defaultProductCategoryId: item.defaultProductCategoryId ?? null,
    defaultProductCategoryName: item.defaultProductCategoryName ?? null,
    intelligenceFilter: item.intelligenceFilter ?? null,
    discountPercentageCredit: item.discountPercentageCredit ?? null,
    deductionTypeInvoicePatternForDiscount:
      item.deductionTypeInvoicePatternForDiscount ?? null,
    discountPercentageDeduction: item.discountPercentageDeduction ?? null,
    discountTermAgreementDays: item.discountTermAgreementDays ?? null,
    additionalFieldMapping: item.additionalFieldMapping ?? null,
    discountTermAgreementDaysTolerance:
      item.discountTermAgreementDaysTolerance ?? null,
    distributionCentersCount: item.distributionCentersCount ?? 0,
  });
}

function toPurchaserMutationParams(
  params: CreateMasterDataPurchaserDTO | UpdateMasterDataPurchaserDTO,
) {
  return {
    purchaserName: params.purchaserName,
    isActive: params.isActive,
    searchKeywords: params.searchKeywords ?? null,
    defaultProductCategoryId: params.defaultProductCategoryId ?? null,
    metadata: params.metadata ?? null,
    intelligenceFilter: params.intelligenceFilter ?? null,
    discountPercentageCredit: params.discountPercentageCredit ?? null,
    deductionTypeInvoicePatternForDiscount:
      params.deductionTypeInvoicePatternForDiscount ?? null,
    discountPercentageDeduction: params.discountPercentageDeduction ?? null,
    discountTermAgreementDays: params.discountTermAgreementDays ?? null,
    additionalFieldMapping: params.additionalFieldMapping ?? null,
    discountTermAgreementDaysTolerance:
      params.discountTermAgreementDaysTolerance ?? null,
  };
}

export class MasterDataPurchaserRepository
  implements IMasterDataPurchaserRepository
{
  private readonly client: ApolloClient;

  constructor(
    client: ApolloClient = MasterDataGraphQlAdapter.getInstance().getClient(),
  ) {
    this.client = client;
  }

  async getPurchasers(
    params: GetMasterDataPurchasersDTO,
    brandKey?: string | null,
  ): Promise<MasterDataPurchasersListResult> {
    const requestBrandKey = brandKey?.trim() || null;
    const { data, error } = await this.client.query<{
      masterDataPurchasers: {
        items: IMasterDataPurchaser[];
      };
    }>({
      query: GET_MASTER_DATA_PURCHASERS,
      variables: {
        params: {
          search: params.search ?? null,
          includeInactive: params.includeInactive ?? true,
        },
      },
      fetchPolicy: "network-only",
      errorPolicy: "all",
      ...(requestBrandKey ? { context: { brandKey: requestBrandKey } } : {}),
    });

    throwIfMasterDataGraphQLError({ error });

    const response = data?.masterDataPurchasers;
    if (!response) {
      throw new Error("Failed to fetch master data purchasers");
    }

    return {
      items: (response.items ?? []).map(mapPurchaserItem),
    };
  }

  async createPurchaser(
    params: CreateMasterDataPurchaserDTO,
  ): Promise<MasterDataPurchaser> {
    return this.putPurchaser(
      CREATE_MASTER_DATA_PURCHASER,
      {
        purchaserKey: params.purchaserKey,
        ...toPurchaserMutationParams(params),
      },
      (data) => data?.createMasterDataPurchaser,
      "Failed to create purchaser",
    );
  }

  async updatePurchaser(
    params: UpdateMasterDataPurchaserDTO,
  ): Promise<MasterDataPurchaser> {
    return this.putPurchaser(
      UPDATE_MASTER_DATA_PURCHASER,
      {
        purchaserId: params.purchaserId,
        ...toPurchaserMutationParams(params),
      },
      (data) => data?.updateMasterDataPurchaser,
      "Failed to update purchaser",
    );
  }

  private async putPurchaser(
    mutation: DocumentNode,
    mutationParams: Record<string, unknown>,
    pickResponse: (
      data:
        | {
            createMasterDataPurchaser?: IMasterDataPurchaser;
            updateMasterDataPurchaser?: IMasterDataPurchaser;
          }
        | null
        | undefined,
    ) => IMasterDataPurchaser | null | undefined,
    fallbackMessage: string,
  ): Promise<MasterDataPurchaser> {
    const result = await this.client.mutate<{
      createMasterDataPurchaser?: IMasterDataPurchaser;
      updateMasterDataPurchaser?: IMasterDataPurchaser;
    }>({
      mutation,
      variables: { params: mutationParams },
      errorPolicy: "all",
    });

    throwIfMasterDataMutationError(result.error);

    return mapPurchaserItem(
      requireMasterDataMutationData(
        pickResponse(result.data),
        result.error,
        fallbackMessage,
      ),
    );
  }
}
